import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';

import {
  StyledFormGroup,
  StyledInput,
  StyledButton,
  StyledLink,
} from '@selfserve/react-ui';
import {
  buttonIds,
  links,
  routesPaths,
  sendFunnelAnalytics,
} from '@selfserve/react-utils';
import styled from '@emotion/styled';
import { FormValues } from '../feature/get-started';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { storageKeys } from '@selfserve/react-hooks';

interface Props {
  isLoading: boolean;
  formik: FormikProps<FormValues>;
  showPrivacyFooter: boolean;
}

const StyledFooterText = styled.p({
  margin: 0,
  fontFamily: 'Lato, sans-serif',
  fontSize: '16px',
  lineHeight: '21px',
  color: '#333',
  textAlign: 'center',
});

const StyledOutSourceLink = styled.a({
  color: '#006EFA',
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'none',
  fontFamily: 'Lato, sans-serif',
});

const StyledRedirectLink = styled(StyledLink)(() => ({
  fontFamily: 'Lato, sans-serif',
}));

export const GetStartedForm = ({
  formik: { handleSubmit, handleChange, handleBlur, values, errors },
  isLoading,
  showPrivacyFooter,
}: Props) => {
  const { t } = useTranslation();
  const { desiredPackageId } = useParams();

  const onContinueClick = useCallback(() => {
    if (!errors.email) {
      sessionStorage.removeItem(storageKeys.SOCIAL_TYPE);
      sendFunnelAnalytics({
        type: 'buttonClicked',
        page: '',
        event: 'signupMethodSelected',
        properties: {
          method: 'manual',
        }
      }, desiredPackageId);
    }
  }, [desiredPackageId, errors.email]);

  return (
    <form className="get-started-form" onSubmit={handleSubmit}>
      <StyledFormGroup>
        <StyledInput
          id="email"
          name="email"
          inputLabel={t('get-started.work-email')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
          error={!!errors.email}
          formHelperText={errors.email}
          inputProps={{ 'aria-label': t('aria.email') }}
          autoFocus
          fullWidth
          placeholder={'example@kaltura.com'}
        />
      </StyledFormGroup>
      <StyledButton
        id={buttonIds.getStartedContinue}
        className="continue-button"
        type="submit"
        loading={isLoading}
        fullWidth
        onClick={onContinueClick}
      >
        {t('common.continue')}
      </StyledButton>
      <div className="login-container">
        <StyledFooterText>
          {t('get-started.already-have-an-account')}{' '}
          <StyledRedirectLink id={buttonIds.getStartedLogin} to={routesPaths.LOGIN}>
            {t('common.login')}
          </StyledRedirectLink>
        </StyledFooterText>
        {showPrivacyFooter && <StyledFooterText>
          {t('create-account-v2.by-proceeding-you-agree')}{' '}
          <StyledOutSourceLink href={links.SERVICE_AGREEMENT} target="_blank">
            {t('common.terms-of-service')}
          </StyledOutSourceLink>{' '}
          {t('common.and')}{' '}
          <StyledOutSourceLink href={links.PRIVACY_POLICY} target="_blank">
            {t('common.privacy-policy')}
          </StyledOutSourceLink>
        </StyledFooterText>}
      </div>
    </form>
  );
};
